import { useEffect } from 'react';
import { CapTableInfo } from 'common/types/capTable';
import {
  FULLY_DILUTED_TOTAL_CROSS_REFERENCE_CONFIG,
  NET_SHARES_CROSS_REFERENCE_CONFIG,
} from 'pages/CapTable/cross-references/configs';
import { useCrossReferenceStore } from 'store/useCrossReferenceStore';
import type {
  CrossReferenceCellKey,
  CrossReferenceConfig,
  CrossReferenceState,
  CrossReferenceWireEntry,
  CrossReferenceWireMap,
} from './types';
import { wireEntryToState } from './utils/serialize';

const mergeWireMap = (
  out: Record<CrossReferenceCellKey, CrossReferenceState>,
  parentType: string,
  parentId: number | string | null | undefined,
  wireMap: CrossReferenceWireMap | null | undefined
): void => {
  if (parentId == null || !wireMap) {
    return;
  }
  Object.entries(wireMap).forEach(([alias, entry]) => {
    const cellKey = `${parentType}:${parentId}:${alias}`;
    // eslint-disable-next-line no-param-reassign
    out[cellKey] = wireEntryToState(entry as CrossReferenceWireEntry);
  });
};

// Build a blank state for a cell that has a known config but no saved data
// yet. Mirrors the store's internal seedIfMissing shape so editing the popup
// flows into the same store slot as a hydrated state would.
const buildBlankState = (config: CrossReferenceConfig): CrossReferenceState => ({
  rowMode: config.rowMode,
  columnsConfig: config.columns,
  rows: [
    {
      position: 0,
      value: null,
      metadata: {},
      isPlaceholder: config.rowMode === 'multi_row',
    },
  ],
  isDirty: false,
});

const seedIfAbsent = (
  out: Record<CrossReferenceCellKey, CrossReferenceState>,
  cellKey: CrossReferenceCellKey,
  config: CrossReferenceConfig
): void => {
  if (!out[cellKey]) {
    // eslint-disable-next-line no-param-reassign
    out[cellKey] = buildBlankState(config);
  }
};

// Seeds the cross-reference store from a cap-table detail response. Runs
// whenever `captableInfo` changes identity. Covers both host features:
//   - CapTable-level cross_references (for fully_diluted_total)
//   - FundOwnershipDetail-level cross_references (for net_shares)
//
// For every cell that has a known cross-ref config we ALSO seed a blank state
// when no saved wire data exists. Without this, the popup's reverseParser
// calls setRows on a missing cellKey, which is a silent no-op -- first-time
// edits would never validate or persist.
const useHydrateCaptableCrossReferences = (captableInfo: CapTableInfo | null | undefined): void => {
  const hydrate = useCrossReferenceStore(store => store.hydrate);

  useEffect(() => {
    if (!captableInfo) {
      return;
    }

    const stateByCellKey: Record<CrossReferenceCellKey, CrossReferenceState> = {};

    mergeWireMap(
      stateByCellKey,
      'captable',
      captableInfo.id,
      captableInfo.cross_references as CrossReferenceWireMap | null | undefined
    );
    if (captableInfo.id != null) {
      seedIfAbsent(
        stateByCellKey,
        `captable:${captableInfo.id}:fully_diluted_total`,
        FULLY_DILUTED_TOTAL_CROSS_REFERENCE_CONFIG
      );
    }

    const fods = captableInfo.fund_ownership?.fund_ownership_detail ?? [];
    fods.forEach(fod => {
      mergeWireMap(
        stateByCellKey,
        'fundownershipdetail',
        fod.id,
        fod.cross_references as CrossReferenceWireMap | null | undefined
      );
      if (fod?.id != null) {
        seedIfAbsent(stateByCellKey, `fundownershipdetail:${fod.id}:net_shares`, NET_SHARES_CROSS_REFERENCE_CONFIG);
      }
    });

    hydrate(stateByCellKey);
  }, [captableInfo, hydrate]);
};

export default useHydrateCaptableCrossReferences;
